import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { useErpGroupHomeworkOverviewQuery } from 'core/graphql';

import { IHomeworkLessonStats } from './homeworkModel';

interface IGroupHomeworkOverviewHook {
  stats: IHomeworkLessonStats[];
  /** Same rows keyed by `lessonId` for O(1) joins with `GroupLessonsQuery`. */
  byLessonId: Map<string, IHomeworkLessonStats>;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Spec 0012 — the «Домашние задания» tab of the group card: per-lesson
 * homework aggregates (`groupHomeworkOverviewForErp`), one row per lesson of
 * the group. Lesson names/dates come from the sibling `useGroupLessons` query;
 * the tab joins the two by `lessonId`.
 */
export const useGroupHomeworkOverview = (
  groupId?: string,
): IGroupHomeworkOverviewHook => {
  const { data, loading, error, refetch } = useErpGroupHomeworkOverviewQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const stats = useMemo(() => data?.groupHomeworkOverviewForErp ?? [], [
    data?.groupHomeworkOverviewForErp,
  ]);
  const byLessonId = useMemo(
    () => new Map(stats.map((row) => [row.lessonId, row])),
    [stats],
  );

  return {
    stats,
    byLessonId,
    loading,
    error,
    refetch: () => {
      refetch();
    },
  };
};
